import {
  ArticleMode,
  ArticleSseEvent,
  CriticActivity,
  GenerationProgressRow,
  GenerationStage,
} from '../../types/articleGeneration';

export interface GenerationProgress {
  generationId: string | null;
  writerModel: string | null;
  stage: GenerationStage | null;
  stagesDone: GenerationStage[];
  stageDurations: Partial<Record<GenerationStage, number>>; // ms
  stageStartedAt: number | null;
  section: number; // current writing section (0 = lead)
  totalSections: number;
  latestHeading: string;
  retry: { stage: string; attempt: number; maxAttempts: number } | null;
  critique: {
    target: 'skeleton' | 'article';
    round: number;
    positive: number;
    total: number;
  } | null;
  critiqueSkipped: boolean;
  critiqueSkippedReason: string | null;
  criticRound: number | null;
  critics: CriticActivity[]; // live per-critic activity for the current round
  hop: number | null; // chunked execution: which server-side part is running
  finished: boolean;
  error: string | null;
}

export function createInitialProgress(headingCount: number): GenerationProgress {
  return {
    generationId: null,
    writerModel: null,
    stage: null,
    stagesDone: [],
    stageDurations: {},
    stageStartedAt: null,
    section: 0,
    totalSections: headingCount + 1, // + lead
    latestHeading: '',
    retry: null,
    critique: null,
    critiqueSkipped: false,
    critiqueSkippedReason: null,
    criticRound: null,
    critics: [],
    hop: null,
    finished: false,
    error: null,
  };
}

/** Reconstruct a progress view from the persisted row (polling / reconnect). */
export function progressFromRow(
  row: GenerationProgressRow | null | undefined,
  headingCount: number
): GenerationProgress {
  const base = createInitialProgress(headingCount);
  if (!row) return base;
  return {
    ...base,
    stage: row.stage ?? null,
    section: row.section ?? 0,
    totalSections: row.totalSections ?? base.totalSections,
    latestHeading: row.latestHeading ?? '',
    writerModel: row.writerModel ?? null,
    critique: row.critique
      ? {
          target: (row.critique.target as 'skeleton' | 'article') ?? 'article',
          round: row.critique.round,
          positive: row.critique.positive,
          total: row.critique.total,
        }
      : null,
    critics: row.critics ?? [],
    criticRound: row.criticRound ?? null,
    critiqueSkipped: !!row.criticsSkipped,
    critiqueSkippedReason: row.criticsSkippedReason ?? null,
    hop: row.hop ?? null,
  };
}

function closeStage(state: GenerationProgress, nowMs: number): GenerationProgress {
  if (state.stage === null) return state;
  return {
    ...state,
    stagesDone: [...state.stagesDone, state.stage],
    stageDurations: {
      ...state.stageDurations,
      [state.stage]: nowMs - (state.stageStartedAt ?? nowMs),
    },
    stage: null,
    stageStartedAt: null,
  };
}

export function progressReducer(
  state: GenerationProgress,
  event: ArticleSseEvent,
  nowMs: number
): GenerationProgress {
  switch (event.kind) {
    case 'content':
      // Streamed body text no longer drives a live preview; it only tells us a
      // stage is producing output, so clear any retry banner.
      return { ...state, retry: null };
    case 'generation':
      return {
        ...state,
        generationId: event.id,
        writerModel: event.writerModel ?? state.writerModel,
      };
    case 'critic_progress': {
      // A new round resets the live critic list.
      const base = state.criticRound === event.round ? state.critics : [];
      const others = base.filter((c) => c.slot !== event.slot);
      const critics = [
        ...others,
        {
          slot: event.slot,
          label: event.label,
          model: event.model,
          state: event.state,
          verdict: event.verdict,
          ok: event.ok,
        },
      ].sort((a, b) => a.slot - b.slot);
      return { ...state, criticRound: event.round, critics };
    }
    case 'retry':
      return {
        ...state,
        retry: { stage: event.stage, attempt: event.attempt, maxAttempts: event.maxAttempts },
      };
    case 'status': {
      const base =
        state.stage !== null && state.stage !== event.stage ? closeStage(state, nowMs) : state;
      return {
        ...base,
        stage: event.stage,
        stageStartedAt: base.stage === event.stage ? base.stageStartedAt : nowMs,
        section: event.section ?? base.section,
        totalSections: event.totalSections ?? base.totalSections,
        latestHeading: event.heading ?? base.latestHeading,
        retry: null,
      };
    }
    case 'critique':
      return {
        ...state,
        critique: {
          target: event.target,
          round: event.round,
          positive: event.positive,
          total: event.total,
        },
        critiqueSkipped: event.total === 0 ? true : state.critiqueSkipped,
        critiqueSkippedReason: event.total === 0
          ? (event.reason ?? state.critiqueSkippedReason)
          : state.critiqueSkippedReason,
      };
    case 'handoff':
      // The server carries on in a new invocation; this stream is ending.
      return { ...state, hop: event.hop };
    case 'cancelled':
      return { ...closeStage(state, nowMs), finished: true };
    case 'needs_review':
      // The run pauses for the user; the workspace takes over from the row.
      return { ...closeStage(state, nowMs), finished: true };
    case 'done':
      return { ...closeStage(state, nowMs), finished: true };
    case 'error':
      return { ...closeStage(state, nowMs), error: event.message };
    case 'result':
      return state;
  }
}

export const WEIGHTS: Record<ArticleMode, Partial<Record<GenerationStage, number>>> = {
  full_article: {
    structure: 0.1,
    briefs: 0.1,
    plan_review: 0.05,
    writing: 0.5,
    article_critique: 0.2,
    language_polish: 0.05,
  },
  headings: { structure: 0.6, plan_review: 0.25, language_polish: 0.15 },
  headings_briefs: { structure: 0.3, briefs: 0.35, plan_review: 0.2, language_polish: 0.15 },
};

export function progressFraction(p: GenerationProgress, mode: ArticleMode): number {
  if (p.finished) return 1;
  const weights = WEIGHTS[mode];
  let fraction = 0;
  for (const stage of p.stagesDone) fraction += weights[stage] ?? 0;
  if (p.stage === 'writing' && p.totalSections > 0) {
    fraction += (weights.writing ?? 0) * (p.section / p.totalSections);
  }
  return Math.min(fraction, 1);
}
